import { useMenuStore } from '@/stores/menuStore'
import type { Category, Product } from '@/types'
import { Plus, Edit2, Trash2, Package, Check, X } from 'lucide-react'

interface ProductListProps {
  products: Product[]
  selectedCategory: Category | null
  onAddProduct: () => void
  onEditProduct: (product: Product) => void
  isLoading: boolean
}

export default function ProductList({
  products,
  selectedCategory,
  onAddProduct,
  onEditProduct,
  isLoading,
}: ProductListProps) {
  const { toggleProductAvailability, deleteProduct } = useMenuStore()

  const handleToggleAvailability = async (product: Product) => {
    try {
      await toggleProductAvailability(product.id)
    } catch (error) {
      // Error handled by store
    }
  }

  const handleDelete = async (product: Product) => {
    if (window.confirm(`Sei sicuro di voler eliminare "${product.name}"?`)) {
      try {
        await deleteProduct(product.id)
      } catch (error) {
        // Error handled by store
      }
    }
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(price)
  }

  return (
    <div className="bg-white dark:bg-[#25252d] rounded-2xl border border-gray-200 dark:border-white/[0.08] p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {selectedCategory ? selectedCategory.name : 'Tutti i Prodotti'}
          </h2>
          <p className="text-sm text-gray-500 dark:text-white/40 mt-1">
            {products.length} {products.length === 1 ? 'prodotto' : 'prodotti'}
          </p>
        </div>
        <button
          onClick={onAddProduct}
          className="px-4 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition flex items-center gap-2 font-medium text-sm"
        >
          <Plus size={16} />
          Aggiungi Prodotto
        </button>
      </div>

      {/* Product List */}
      {isLoading && products.length === 0 ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-orange-500 border-t-transparent mb-3"></div>
          <p className="text-gray-500 dark:text-white/40">Caricamento prodotti...</p>
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center mx-auto mb-4">
            <Package size={32} className="text-gray-400 dark:text-white/30" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Nessun prodotto</h3>
          <p className="text-gray-500 dark:text-white/40 mb-4 text-sm">
            {selectedCategory
              ? `Aggiungi il primo prodotto in "${selectedCategory.name}"`
              : 'Inizia aggiungendo il tuo primo prodotto al menu'}
          </p>
          <button
            onClick={onAddProduct}
            className="inline-flex items-center px-4 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition gap-2 font-medium text-sm"
          >
            <Plus size={16} />
            Aggiungi Prodotto
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {products.map((product) => (
            <div
              key={product.id}
              className={`border rounded-xl p-4 transition-all duration-200 ${
                product.available
                  ? 'border-gray-200 dark:border-white/[0.06] bg-white dark:bg-white/[0.02] hover:border-gray-300 dark:hover:border-white/[0.1]'
                  : 'border-gray-200 dark:border-white/[0.04] bg-gray-50 dark:bg-white/[0.01] opacity-60'
              }`}
            >
              <div className="flex gap-4">
                {/* Product Image */}
                <div className="flex-shrink-0">
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                      className="w-20 h-20 object-cover rounded-xl"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-gray-100 dark:bg-white/[0.06] rounded-xl flex items-center justify-center">
                      <Package size={24} className="text-gray-400 dark:text-white/30" />
                    </div>
                  )}
                </div>

                {/* Product Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 flex-wrap">
                        {product.name}
                        {!product.available && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 rounded-full font-medium">
                            Non disponibile
                          </span>
                        )}

                        {/* Smart Badges */}
                        {product.isPopular && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-orange-500 text-white rounded-full font-medium">
                            🔥 Più Venduto
                          </span>
                        )}
                        {product.isChefRecommended && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-violet-500 text-white rounded-full font-medium">
                            ⭐ Chef
                          </span>
                        )}
                        {product.isNew && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-blue-500 text-white rounded-full font-medium">
                            🆕 Novità
                          </span>
                        )}
                        {product.isVegan && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500 text-white rounded-full font-medium">
                            🌱 Vegano
                          </span>
                        )}
                        {product.isVegetarian && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-green-500 text-white rounded-full font-medium">
                            🥬 Vegetariano
                          </span>
                        )}
                      </h3>
                      {product.description && (
                        <p className="text-sm text-gray-500 dark:text-white/50 mt-1 line-clamp-2">
                          {product.description}
                        </p>
                      )}

                      {/* Tags & Allergens */}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {product.tags && product.tags.length > 0 && (
                          <>
                            {product.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="text-[10px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                          </>
                        )}
                        {product.allergens && product.allergens.length > 0 && (
                          <>
                            {product.allergens.map((allergen, index) => (
                              <span
                                key={index}
                                className="text-[10px] px-1.5 py-0.5 bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 rounded-full"
                              >
                                ⚠️ {allergen}
                              </span>
                            ))}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Price */}
                    <div className="ml-4 text-right flex-shrink-0">
                      <div className="text-xl font-bold text-orange-500 dark:text-orange-400">
                        {formatPrice(product.price)}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() => handleToggleAvailability(product)}
                      className={`text-xs px-2.5 py-1.5 rounded-lg transition flex items-center gap-1.5 font-medium ${
                        product.available
                          ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-500/30'
                          : 'bg-gray-100 dark:bg-white/[0.06] text-gray-500 dark:text-white/50 hover:bg-gray-200 dark:hover:bg-white/[0.1]'
                      }`}
                    >
                      {product.available ? <Check size={12} /> : <X size={12} />}
                      {product.available ? 'Disponibile' : 'Non disponibile'}
                    </button>
                    <button
                      onClick={() => onEditProduct(product)}
                      className="text-xs px-2.5 py-1.5 rounded-lg bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-500/30 transition flex items-center gap-1.5 font-medium"
                    >
                      <Edit2 size={12} />
                      Modifica
                    </button>
                    <button
                      onClick={() => handleDelete(product)}
                      className="text-xs px-2.5 py-1.5 rounded-lg bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-500/30 transition flex items-center gap-1.5 font-medium"
                    >
                      <Trash2 size={12} />
                      Elimina
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
